import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WmsCustomColours, WmsTheme, WmsThemeService } from './wms-theme.service';

/**
 * Header button + dropdown to pick the sidebar/dashboard colour theme.
 * Only rendered for client 8 (see dense layout). Applies the saved theme on load
 * and removes it again when destroyed, so other clients are never affected.
 */
@Component({
    selector: 'wms-theme-picker',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
    template: `
        <button mat-icon-button type="button" class="wtp-trigger" (click)="toggle($event)" matTooltip="Theme colours"
            [attr.aria-expanded]="open" aria-label="Theme colours">
            <mat-icon>{{ icon }}</mat-icon>
        </button>

        <div class="wtp-panel" *ngIf="open" role="dialog" aria-label="Theme colours">
            <div class="wtp-head">
                <div class="wtp-head-icn"><mat-icon>palette</mat-icon></div>
                <div class="wtp-head-txt">
                    <div class="wtp-title">Theme colours</div>
                    <div class="wtp-sub">Personalise the sidebar and dashboard</div>
                </div>
                <button type="button" class="wtp-close" (click)="open = false" aria-label="Close">
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <div class="wtp-body">
                <!-- 3D switch -->
                <button type="button" class="wtp-depth" (click)="theme.setDepth(!theme.depth3d)"
                    role="switch" [attr.aria-checked]="theme.depth3d">
                    <span class="wtp-depth-icn"><mat-icon>view_in_ar</mat-icon></span>
                    <span class="wtp-depth-txt">
                        <span class="wtp-depth-ttl">3D depth effect</span>
                        <span class="wtp-depth-sub">Glossy, raised cards and menu</span>
                    </span>
                    <span class="wtp-switch" [class.on]="theme.depth3d"><span></span></span>
                </button>

                <div class="wtp-label">Classic</div>
                <div class="wtp-grid">
                    <ng-container *ngFor="let t of theme.classicPresets">
                        <ng-container *ngTemplateOutlet="swatch; context: { $implicit: t }"></ng-container>
                    </ng-container>
                </div>

                <div class="wtp-label">
                    Custom colours
                    <span class="wtp-tag" *ngIf="theme.activeId === 'custom'">In use</span>
                </div>
                <div class="wtp-fields">
                    <label class="wtp-field" *ngFor="let f of fields">
                        <span class="wtp-chip" [style.background]="theme.custom[f.key]">
                            <input type="color" [value]="theme.custom[f.key]"
                                (input)="theme.setCustom(f.key, $any($event.target).value)">
                        </span>
                        <span class="wtp-field-txt">
                            <span class="wtp-field-name">{{ f.label }}</span>
                            <span class="wtp-field-hex">{{ theme.custom[f.key] }}</span>
                        </span>
                    </label>
                </div>
            </div>

            <div class="wtp-foot">
                <button type="button" class="wtp-reset" (click)="theme.reset()">
                    <mat-icon>restart_alt</mat-icon> Reset to default
                </button>
            </div>
        </div>

        <!-- One theme tile: mini sidebar + mini dashboard preview -->
        <ng-template #swatch let-t>
            <button type="button" class="wtp-swatch" [class.wtp-active]="theme.activeId === t.id"
                [class.wtp-3d]="t.depth" (click)="theme.selectPreset(t.id)" [attr.aria-pressed]="theme.activeId === t.id"
                [title]="t.name">
                <span class="wtp-preview" [style.background]="t.pageBg">
                    <span class="wtp-pv-side" [style.background]="sideGradient(t)">
                        <span class="wtp-pv-dot" [style.background]="t.accent"></span>
                        <span class="wtp-pv-pill" [style.background]="t.primary"></span>
                        <span class="wtp-pv-line" [style.background]="t.accent"></span>
                    </span>
                    <span class="wtp-pv-main">
                        <span class="wtp-pv-cards">
                            <span [style.background]="t.total"></span>
                            <span *ngFor="let c of t.cards.slice(0, 3)" [style.background]="c"></span>
                        </span>
                        <span class="wtp-pv-panel"></span>
                    </span>
                    <span class="wtp-pv-tick" *ngIf="theme.activeId === t.id"><mat-icon>check</mat-icon></span>
                </span>
                <span class="wtp-name">{{ t.name }}</span>
            </button>
        </ng-template>
    `,
    styles: [`
        :host { position: relative; display: inline-flex; }

        .wtp-panel {
            position: absolute; top: calc(100% + 10px); right: 0; z-index: 1000;
            width: 392px; max-width: calc(100vw - 24px);
            display: flex; flex-direction: column; max-height: calc(100vh - 96px);
            background: #ffffff; color: #1e293b; border: 1px solid rgba(226, 232, 240, 0.9); border-radius: 18px;
            box-shadow: 0 24px 60px -12px rgba(15, 23, 42, 0.28), 0 2px 6px rgba(15, 23, 42, 0.06);
            overflow: hidden;
        }

        /* Header */
        .wtp-head {
            display: flex; align-items: center; gap: 10px; padding: 14px 14px 12px;
            background: linear-gradient(135deg, #eef2ff 0%, #fdf2f8 50%, #ecfeff 100%);
            border-bottom: 1px solid #eef2f7;
        }
        .wtp-head-icn {
            width: 36px; height: 36px; border-radius: 11px; display: flex; align-items: center; justify-content: center;
            background: linear-gradient(135deg, #7c5cff, #ff4f81); color: #fff;
            box-shadow: 0 6px 14px -4px rgba(124, 92, 255, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.45);
        }
        .wtp-head-icn mat-icon { color: #fff; font-size: 20px; width: 20px; height: 20px; }
        .wtp-head-txt { flex: 1; min-width: 0; }
        .wtp-title { font-size: 15px; font-weight: 700; color: #0f172a; }
        .wtp-sub { font-size: 12px; color: #64748b; }
        .wtp-close { border: 0; background: rgba(255, 255, 255, 0.7); cursor: pointer; color: #475569;
            display: flex; padding: 4px; border-radius: 8px; }
        .wtp-close:hover { background: #fff; }

        .wtp-body { padding: 12px 14px 4px; overflow-y: auto; }

        /* 3D switch */
        .wtp-depth {
            width: 100%; display: flex; align-items: center; gap: 10px; padding: 9px 10px; cursor: pointer; text-align: left;
            border: 1px solid #e2e8f0; border-radius: 12px;
            background: linear-gradient(180deg, #ffffff 0%, #f6f8fc 100%);
            box-shadow: 0 2px 6px -2px rgba(15, 23, 42, 0.08), inset 0 1px 0 #fff;
        }
        .wtp-depth-icn { width: 30px; height: 30px; border-radius: 9px; display: flex; align-items: center; justify-content: center;
            background: #eef2ff; color: #4f46e5; }
        .wtp-depth-icn mat-icon { font-size: 18px; width: 18px; height: 18px; color: #4f46e5; }
        .wtp-depth-txt { flex: 1; display: flex; flex-direction: column; }
        .wtp-depth-ttl { font-size: 13px; font-weight: 600; color: #0f172a; }
        .wtp-depth-sub { font-size: 11.5px; color: #64748b; }
        .wtp-switch { width: 38px; height: 22px; border-radius: 999px; background: #cbd5e1; position: relative;
            transition: background .2s; flex-shrink: 0; box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.15); }
        .wtp-switch span { position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%;
            background: #fff; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3); transition: transform .2s; }
        .wtp-switch.on { background: linear-gradient(90deg, #6366f1, #8b5cf6); }
        .wtp-switch.on span { transform: translateX(16px); }

        /* Section labels */
        .wtp-label { display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 700;
            letter-spacing: 0.07em; text-transform: uppercase; color: #64748b; margin: 14px 0 8px; }
        .wtp-new { letter-spacing: 0.02em; font-size: 10px; color: #fff; padding: 1px 7px; border-radius: 999px;
            background: linear-gradient(90deg, #ff4f81, #7c5cff); }
        .wtp-tag { text-transform: none; letter-spacing: 0; font-weight: 600; font-size: 11px;
            background: #dcfce7; color: #166534; padding: 1px 8px; border-radius: 999px; }

        /* Theme tiles */
        .wtp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .wtp-swatch {
            display: flex; flex-direction: column; gap: 5px; padding: 5px; text-align: left; cursor: pointer; min-width: 0;
            background: #fff; border: 1.5px solid #e9edf3; border-radius: 12px;
            transition: border-color .15s, box-shadow .15s, transform .15s;
        }
        .wtp-swatch:hover { transform: translateY(-2px); border-color: #c7d2fe;
            box-shadow: 0 8px 18px -10px rgba(15, 23, 42, 0.35); }
        .wtp-swatch.wtp-active { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18); }

        .wtp-preview { position: relative; display: flex; height: 50px; border-radius: 8px; overflow: hidden;
            border: 1px solid rgba(15, 23, 42, 0.06); }
        .wtp-pv-side { width: 32%; display: flex; flex-direction: column; align-items: center; gap: 4px; padding-top: 6px; }
        .wtp-pv-dot { width: 12px; height: 3px; border-radius: 2px; opacity: .9; }
        .wtp-pv-pill { width: 70%; height: 6px; border-radius: 3px; }
        .wtp-pv-line { width: 60%; height: 3px; border-radius: 2px; opacity: .45; }
        .wtp-pv-main { flex: 1; display: flex; flex-direction: column; gap: 4px; padding: 5px; }
        .wtp-pv-cards { display: flex; gap: 3px; height: 15px; }
        .wtp-pv-cards span { flex: 1; border-radius: 3px; }
        .wtp-pv-panel { flex: 1; border-radius: 4px; background: #ffffff; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08); }
        .wtp-pv-tick { position: absolute; right: 3px; bottom: 3px; width: 16px; height: 16px; border-radius: 50%;
            background: #6366f1; display: flex; align-items: center; justify-content: center;
            box-shadow: 0 2px 4px rgba(99, 102, 241, 0.5); }
        .wtp-pv-tick mat-icon { color: #fff; font-size: 12px; width: 12px; height: 12px; }

        /* glossy tiles for 3D themes */
        .wtp-3d .wtp-pv-cards span, .wtp-3d .wtp-pv-pill {
            background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0) 60%) !important;
            box-shadow: 0 2px 3px -1px rgba(15, 23, 42, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }
        .wtp-3d .wtp-pv-cards span { background-blend-mode: normal; }

        .wtp-name { font-size: 11.5px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden;
            text-overflow: ellipsis; padding: 0 2px 1px; }

        /* Custom colour fields */
        .wtp-fields { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
        .wtp-field { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border: 1px solid #e9edf3;
            border-radius: 12px; cursor: pointer; background: linear-gradient(180deg, #fff 0%, #f9fafc 100%); }
        .wtp-field:hover { border-color: #c7d2fe; }
        .wtp-chip { position: relative; width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; overflow: hidden;
            box-shadow: 0 2px 5px -1px rgba(15, 23, 42, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.5);
            border: 1px solid rgba(15, 23, 42, 0.08); }
        .wtp-chip input[type=color] { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer;
            border: 0; padding: 0; }
        .wtp-field-txt { display: flex; flex-direction: column; min-width: 0; }
        .wtp-field-name { font-size: 12px; font-weight: 600; }
        .wtp-field-hex { font-size: 11px; color: #64748b; font-family: monospace; text-transform: uppercase; }

        .wtp-foot { display: flex; justify-content: flex-end; padding: 10px 14px 12px; border-top: 1px solid #f1f5f9; }
        .wtp-reset { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #e2e8f0;
            background: linear-gradient(180deg, #fff 0%, #f6f8fc 100%); color: #334155; font-size: 12.5px; font-weight: 600;
            padding: 6px 12px; border-radius: 9px; cursor: pointer; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06); }
        .wtp-reset:hover { border-color: #c7d2fe; }
        .wtp-reset mat-icon { font-size: 16px; width: 16px; height: 16px; }
    `]
})
export class WmsThemePickerComponent implements OnInit, OnDestroy {
    /** Material icon name for the header trigger button. */
    @Input() icon = 'palette';

    open = false;

    readonly fields: { key: keyof WmsCustomColours; label: string }[] = [
        { key: 'sidebar', label: 'Sidebar' },
        { key: 'primary', label: 'Primary' },
        { key: 'accent', label: 'Accent' },
        { key: 'pageBg', label: 'Page background' }
    ];

    constructor(public theme: WmsThemeService, private _el: ElementRef<HTMLElement>) { }

    ngOnInit(): void {
        this.theme.enable();
    }

    ngOnDestroy(): void {
        this.theme.disable();
    }

    /** Mini sidebar background for a preview tile. */
    sideGradient(t: WmsTheme): string {
        return t.family === 'light'
            ? `linear-gradient(180deg, #ffffff 0%, ${t.sidebar} 100%)`
            : t.sidebar;
    }

    toggle(event: Event): void {
        event.stopPropagation();
        this.open = !this.open;
    }

    @HostListener('document:click', ['$event'])
    onDocClick(event: Event): void {
        if (this.open && !this._el.nativeElement.contains(event.target as Node)) {
            this.open = false;
        }
    }

    @HostListener('document:keydown.escape')
    onEsc(): void {
        this.open = false;
    }
}
